import JSON5 from 'json5';
import { entries, omitBy, pickBy } from 'lodash-es';
import { nanoid } from 'nanoid';

export type { memoize } from 'lodash-es';

export type ElementType<T> = T extends Array<infer U> ? U : never;

/**
 * @internal
 * A type guard that checks if a value is not undefined or null.
 */
export type NonVoid<T> = [T] extends [void] ? never : T;

export const EMPTY_ARRAY = Object.freeze([] as const);

export const assign = Object.assign;

export function toMap<const K extends PropertyKey, V>(object: Record<K, V>): Map<K, V> {
  const entries = Object.entries(object) as Array<[K, V]>;
  return new Map<K, V>(entries);
}

/**
 * A simple hash function for generating a unique cache key.
 */
export function quickHash(value: string | unknown): string {
  const payload = typeof value === 'string' ? value : JSON.stringify(value);
  return cyrb64Hash(payload);
}

export function makeKey(name: string, value: unknown): string {
  const objKey = quickHash(value);
  return `${name}:${objKey}`;
}

// cyrb53 (c) 2018 bryc (github.com/bryc). License: Public domain. Attribution appreciated.
// A fast and simple 64-bit (or 53-bit) string hash function with decent collision resistance.
// Largely inspired by MurmurHash2/3, but with a focus on speed/simplicity.
// See https://stackoverflow.com/questions/7616461/generate-a-hash-from-string-in-javascript/52171480#52171480
// https://github.com/bryc/code/blob/master/jshash/experimental/cyrb53.js
const cyrb64 = (str: string, seed = 0) => {
  let h1 = 0xdeadbeef ^ seed,
    h2 = 0x41c6ce57 ^ seed;
  for (let i = 0, ch; i < str.length; i++) {
    // eslint-disable-next-line unicorn/prefer-code-point
    ch = str.charCodeAt(i);
    h1 = Math.imul(h1 ^ ch, 2_654_435_761);
    h2 = Math.imul(h2 ^ ch, 1_597_334_677);
  }
  h1 = Math.imul(h1 ^ (h1 >>> 16), 2_246_822_507);
  h1 ^= Math.imul(h2 ^ (h2 >>> 13), 3_266_489_909);
  h2 = Math.imul(h2 ^ (h2 >>> 16), 2_246_822_507);
  h2 ^= Math.imul(h1 ^ (h1 >>> 13), 3_266_489_909);
  // For a single 53-bit numeric return value we could return
  // 4294967296 * (2097151 & h2) + (h1 >>> 0);
  // but we instead return the full 64-bit value:
  return [h2 >>> 0, h1 >>> 0] as const;
};

// An improved, *insecure* 64-bit hash that's short, fast, and has no dependencies.
// Output is always 14 characters.
export const cyrb64Hash = (str: string, seed = 0) => {
  const [h2, h1] = cyrb64(str, seed);
  return h2.toString(36).padStart(7, '0') + h1.toString(36).padStart(7, '0');
};
export function uuid() {
  return crypto.randomUUID();
}

export function identity<T>(value: T): T {
  return value;
}

/**
 * Normalize a contact phone to E.164 (`+<digits>`, no spaces or punctuation) for downstream systems
 * (GDS, telephony) that reject loosely-formatted numbers.
 *
 * This is a pragmatic normalizer, not a full libphonenumber parse: it strips formatting, guarantees a
 * single leading `+`, fixes the common North-American case where a caller typed the `1` country code into
 * the national-number field on top of a `+1` dial-code selector (a duplicated `+1 1XXXXXXXXXX`), and — when
 * the value carries an explicit space-delimited dial code (as the phone form emits, e.g. "+44 07911 123456")
 * — strips a leading trunk-`0` from the national number for NON-NANP codes (UK/EU nationals are
 * conventionally written with a leading 0 that is not part of E.164). Full international correctness
 * (extensions, every national format) would need libphonenumber.
 *
 * @example toE164('+1 12679183009')      // '+12679183009'  (duplicated +1 collapsed)
 * @example toE164('+1 (267) 918-3009')   // '+12679183009'
 * @example toE164('2679183009')          // '+12679183009'  (bare NANP number gets its country code)
 * @example toE164('+44 7911 123456')     // '+447911123456' (non-NANP)
 * @example toE164('+44 07911 123456')    // '+447911123456' (trunk-0 stripped for non-NANP)
 */
export function toE164(raw: string): string {
  const trimmed = (raw ?? '').trim();
  if (!trimmed) return '';

  // When the value carries an explicit, space-delimited dial code (the phone form emits "<+code> <national>")
  // and the code is NOT NANP, strip a leading trunk-`0` from the national number — it is how UK/EU nationals
  // are conventionally written but is not part of E.164. NANP (+1) and inputs without that boundary fall
  // through to the digit rules below (we can't tell a trunk-0 from a significant digit without the boundary).
  const split = /^\+(\d{1,3})\s+(.+)$/.exec(trimmed);
  if (split && split[1] !== '1') {
    const national = split[2]!.replaceAll(/\D/g, '').replace(/^0+/, '');
    return national ? `+${split[1]}${national}` : '';
  }

  let digits = trimmed.replaceAll(/\D/g, '');
  if (!digits) return '';
  // North American Numbering Plan (+1): the national significant number is exactly 10 digits.
  //  - 10 digits          → bare national number, prepend the `1` country code.
  //  - 12 digits, "11…"   → "+1" plus an 11-digit national that re-includes the `1` → drop the dup.
  // 11 digits starting with `1` is already correct ("1" + 10); everything else passes through.
  if (digits.length === 10) {
    digits = `1${digits}`;
  } else if (digits.length === 12 && digits.startsWith('11') && digits[2]! >= '2' && digits[2]! <= '9') {
    // "+1" + an 11-digit national that re-includes the country code. NANP area codes start 2–9, so this
    // only collapses a genuine duplicated +1 — not some other 12-digit number that happens to start "11".
    digits = digits.slice(1);
  }
  return `+${digits}`;
}

// export type Result<T, E = Error> = Success<T> | Failure<E>;
export abstract class BaseResult<T, E> {
  static flatten<T, E1, E2>(result: Result<Result<T, E1>, E2>): Result<T, E1 | E2> {
    if (Success.is(result)) return result.data as Result<T, E1 | E2>;
    return result;
  }
  // if (this.isSuccess(result)) return result.data;
  // throw result.error;

  abstract isSuccess: boolean;
  // if (this.isSuccess(result)) return result.data;
  // return errorHandler(result.error);
  abstract error: E | undefined;
  // if (this.isSuccess(result)) return this.success(mapper(result.data));
  // else return this.failure(errorHandler(result.error));
  abstract data: T | undefined;
  // {
  //   return this.transform(result, mapper, identity);
  abstract isFailure: boolean;
  // return this.transform(result, (data) => data, errorMapper);

  abstract unwrap(): T;
  // if (this.isSuccess(result)) return mapper(result.data);
  // return this.failure(result.error);

  abstract catchError(errorHandler: (error: E) => T): T;

  abstract transform<T2, E2>(mapper: (data: T) => T2, errorHandler: (error: E) => E2): Result<T2, E2>;

  abstract map<T2>(mapper: (data: T) => T2): Result<T2, E>;

  // }
  abstract mapError<E2>(errorMapper: (error: E) => E2): Result<T, E2>;

  abstract flatMap<T2, E2>(mapper: (data: T) => Result<T2, E2>): Result<T2, E | E2>;
  // if (this.isSuccess(result)) {
  //   return result.data;
  // }
  // return this.failure(result.error);

  abstract orElse(result: Result<T, E>, elseHandler: (error: E) => T): T;
  //   if (this.isSuccess(result)) {
  //     return result.data;
  //   }
  //   return elseHandler(result.error);
}

export const ok = <T, E>(data: T): Result<T, E> => new Success(data);

export const failure = <E, T = unknown>(error: E): Result<T, E> => new Failure(error);

export const defaultErrorHandler = <E extends Error>(error: unknown): E => {
  if (error instanceof Error) return error as E;
  return new Error(String(error)) as E;
};

export const fromFn = <T, E extends Error = Error>(
  fn: () => T,
  errorHandler = defaultErrorHandler<E>,
): Result<T, E> => {
  try {
    return ok(fn());
  } catch (error) {
    const handler = errorHandler ?? defaultErrorHandler<E>;
    return failure(handler(error)) as Result<T, E>;
  }
};

export const Result = {
  fromFn,
  ok,
  failure,
  is<T, E = unknown>(value: unknown): value is BaseResult<T, E> {
    return value instanceof BaseResult;
  },
};

export class Success<T> extends BaseResult<T, unknown> {
  static is<T>(result: unknown): result is Success<T> {
    return result instanceof Success;
  }

  constructor(
    readonly data: T,
    readonly error: unknown = undefined,
  ) {
    super();
  }

  isSuccess = true;
  isFailure = false;

  override unwrap() {
    return this.data;
  }

  override catchError(_errorHandler: (error: unknown) => T) {
    return this.data;
  }

  override transform<T2, E2>(mapper: (data: T) => T2, _errorHandler: (error: unknown) => E2): Result<T2, E2> {
    return Result.ok(mapper(this.data)) as Result<T2, E2>;
  }

  override map<T2>(mapper: (data: T) => T2): Result<T2, unknown> {
    return Result.ok(mapper(this.data)) as Result<T2, unknown>;
  }

  override mapError<E2>(_errorMapper: (error: unknown) => E2): Result<T, E2> {
    return Result.ok(this.data) as Result<T, E2>;
  }

  override flatMap<T2, E2>(mapper: (data: T) => Result<T2, E2>): Result<T2, E2 | unknown> {
    return mapper(this.data) as Result<T2, E2 | unknown>;
  }

  override orElse(_result: Result<T, unknown>, _elseHandler: (error: unknown) => T) {
    return this.data;
  }
}

export type Result<T, E> = Success<T> | Failure<E>;

export class Failure<E> extends BaseResult<unknown, E> {
  static isFailure<E>(value: unknown): value is Failure<E> {
    return value instanceof Failure;
  }

  constructor(
    readonly error: E,
    readonly data: unknown = undefined,
  ) {
    super();
  }

  isFailure = true;
  isSuccess = false;

  override unwrap() {
    throw this.error;
  }

  override catchError(errorHandler: (error: E) => unknown) {
    return errorHandler(this.error);
  }

  override transform<T2, E2>(_mapper: (data: unknown) => T2, errorHandler: (error: E) => E2): Result<T2, E2> {
    return Result.failure(errorHandler(this.error)) as Result<T2, E2>;
  }

  override map<T2>(_mapper: (data: unknown) => T2): Result<T2, E> {
    return Result.failure(this.error) as Result<T2, E>;
  }

  override mapError<E2, T2 = unknown>(errorMapper: (error: E) => E2): Result<T2, E2> {
    return Result.failure(errorMapper(this.error));
  }

  override flatMap<T2, E2>(_mapper: (data: unknown) => Result<T2, E2>): Result<T2, E2> {
    return Result.failure(this.error) as Result<T2, E2>;
  }

  override orElse(_result: Result<unknown, E>, elseHandler: (error: E) => unknown): unknown {
    return elseHandler(this.error);
  }
}

export const some = <T>(value: T): Maybe<T> => new Just(value);

export const none = <T>(): Maybe<T> => new Nothing();

export const Maybe = {
  is<T>(value: unknown): value is Maybe<T> {
    return value instanceof BaseMaybe;
  },
  some,
  nothing: none,
};

export type Maybe<T> = Just<T> | Nothing<T>;

export abstract class BaseMaybe<T> {
  abstract isJust: boolean;
  abstract isNothing: boolean;
  abstract none: T | undefined;

  abstract map<T2>(mapper: (value: T) => T2): Maybe<T2>;
  abstract flatMap<T2>(mapper: (value: T) => Maybe<T2>): Maybe<T2>;
  abstract orElse<M>(elseHandler: () => M): M;
}

export class Just<T> extends BaseMaybe<T> {
  constructor(readonly none: T) {
    super();
  }

  isJust = true;
  isNothing = false;

  override map<T2>(mapper: (value: T) => T2): Maybe<T2> {
    return some(mapper(this.none));
  }

  override flatMap<T2>(mapper: (value: T) => Maybe<T2>): Maybe<T2> {
    return mapper(this.none);
  }

  override orElse<M>(elseHandler: () => M): M {
    return elseHandler();
  }
}

export class Nothing<T> extends BaseMaybe<T> {
  constructor() {
    super();
  }

  isJust = false;
  isNothing = true;
  none: undefined;

  override map<T2>(_mapper: (value: T) => T2): Maybe<T2> {
    return Maybe.nothing();
  }

  override flatMap<T2>(_mapper: (value: T) => Maybe<T2>): Maybe<T2> {
    return Maybe.nothing();
  }

  override orElse<M>(elseHandler: () => M): M {
    return elseHandler();
  }
}

export function shorten(
  string: string,
  {
    maxLength = 256,
    skipMiddle,
    includeOmittedLength = true,
    ellipsis = '...',
  }: { maxLength?: number; includeOmittedLength?: boolean; ellipsis?: string; skipMiddle?: boolean } = {},
): string {
  if (string.length <= maxLength) return string;
  const buf = string.slice(0, Math.max(0, maxLength));
  if (!includeOmittedLength) return buf;
  const rest = string.length - maxLength;
  ellipsis = ellipsis.replace('%1', rest.toString());
  skipMiddle ??= string.length > 1024;
  if (skipMiddle) {
    const fullEllipsis = /^\W/.test(ellipsis) ? ellipsis : `... ${ellipsis} ...`;
    const bufLen = buf.length - fullEllipsis.length;
    const mid = bufLen / 2;
    return `${buf.slice(0, mid)}${fullEllipsis}${buf.slice(mid)}`;
  } else {
    return `${buf}${ellipsis}`;
  }
}

export function tryReadJson(value: string): unknown;

export function tryReadJson<T>(value: T): T;

export function tryReadJson(value: unknown): unknown {
  if (typeof value !== 'string') return value;
  try {
    return JSON.parse(value);
  } catch {}
  if (isLlmJsonString(value)) {
    try {
      return extractJson(value);
    } catch {}
  }
  return value;
}

function extractJson(value: string): unknown {
  const trimmed = value.trim();

  const fenced = /^```(?:json)?\s*([\s\S]*?)\s*```$/i.exec(trimmed)?.[1];
  if (fenced) {
    return JSON5.parse(fenced);
  }

  const inlineFenced = /```(?:json)?\s*([\s\S]*?)\s*```/i.exec(trimmed)?.[1];
  if (inlineFenced) {
    return JSON5.parse(inlineFenced);
  }

  return JSON5.parse(trimmed);
}

export function isLlmJsonString(value: unknown): value is string {
  if (typeof value !== 'string') return false;
  return (
    value.startsWith('{') ||
    value.startsWith('[') ||
    value.startsWith('"') ||
    value.startsWith("'") ||
    value.startsWith('```json')
  );
}

export function tryStringifyJson(value: unknown): string | undefined {
  try {
    return JSON.stringify(value);
  } catch {
    return;
  }
}

/**
 * A simple function to generate a unique ID with an optional prefix.
 */
export function makeId({ prefix = '', length = 21, cleanUnderscores = true } = {}): string {
  let id = nanoid(length);
  if (cleanUnderscores) id = id.replaceAll(/[-_]/g, '');
  return `${prefix ? `${prefix}-` : ''}${id}`;
}

export function disposer(fn: () => void): { [Symbol.dispose]: () => void } {
  return { [Symbol.dispose]: fn };
}

/**
 * Maps the entries of an object to a new object.
 * @param object - The object to map the entries of.
 * @param mapFn - The function to map the entries of the object.
 * @returns The new object with the mapped entries.
 */
export function mapEntries<K extends PropertyKey, V, K_ extends PropertyKey, V_>(
  object: Record<K, V>,
  mapFn: (key: string, value: V) => [K_, V_],
): Record<K_, V_> {
  return Object.fromEntries(entries<V>(object).map(([key, value]) => mapFn(key, value))) as Record<K_, V_>;
}

export function matchesPattern(needle: string, patterns: Array<string | RegExp>) {
  return patterns.some((pattern) => {
    if (typeof pattern === 'string') return needle.toLowerCase() === pattern.toLowerCase();
    return pattern.test(needle);
  });
}

export function prettyJson(value: unknown): string {
  return JSON5.stringify(value, null, 2);
}

export function omitByPattern<T extends object | null | undefined>(obj: T, pattern: RegExp): Partial<T> {
  return omitBy(obj, (_value, key) => typeof key === 'string' && pattern.test(key));
}

export function pickByPattern<T extends Record<string, any> | null | undefined>(obj: T, pattern: RegExp): Partial<T> {
  return pickBy(obj, (_value, key) => typeof key === 'string' && pattern.test(key)) as Partial<T>;
}
